import { useRef } from "react";
import { motion } from "framer-motion";
import { Card, Typography, Box, Tooltip, Button } from "@mui/material";
import { DragIndicator, FileDownloadOutlined, UploadFileOutlined } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import { muiCustomScrollbar } from "data/globalData";
import { logicProfileStoreGroupingAssoc, storeGroupingtypes, storeMastShortNameKey, storeMastStoreCodeKey } from "../../data";
import { GlobalState } from "context/GlobalContext";
import {
	applyStoreGroupingUpload,
	downloadStoreGroupingSample,
	readStoreGroupingFile,
} from "./storeGroupingExcel";

const ACCEPTED_EXCEL_TYPES = ".xlsx,.xls";

export const DragDropStores = ({ storeCodeList, storeFullDataList, profileData, setProfileData }) => {
	const fileInputRef = useRef(null);
	const { snackBar, setSnackBar, setSnackBarOpen } = GlobalState();
	let storeFullDataListObj = storeFullDataList?.reduce((acc, curr) => {
		acc[curr?.[storeMastStoreCodeKey]] = curr;
		return acc;
	}, {});

	const knownStoreCodes =
		storeCodeList?.length > 0
			? storeCodeList
			: storeFullDataList?.map((row) => row?.[storeMastStoreCodeKey])?.filter(Boolean) ?? [];

	const setProfileDataAfterDragging = (data, column) => {
		setProfileData((prev) => {
			return {
				...prev,
				[logicProfileStoreGroupingAssoc]: { ...prev?.[logicProfileStoreGroupingAssoc], [column]: data },
			};
		});
	};

	const handleDragEnd = (event, info, store, source, setSource, target, setTarget) => {
		if (!target) return;
		setSource(source.filter((s) => s !== store));
		setTarget([...target, store]);
	};

	const showSnack = (message, severity = "info") => {
		setSnackBarOpen(true);
		setSnackBar({
			...snackBar,
			message,
			severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		});
	};

	const handleExcelUpload = async (event) => {
		event.preventDefault();
		event.stopPropagation();
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) return;

		const fileName = file.name?.toLowerCase() ?? "";
		if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".xls")) {
			showSnack("Please upload an Excel file (.xlsx or .xls)", "warning");
			return;
		}

		try {
			const uploadedLists = await readStoreGroupingFile(file);
			const { grouping, unknown, duplicates, assignedCount } = applyStoreGroupingUpload({
				uploadedLists,
				knownStores: knownStoreCodes,
				storeFullDataList: storeFullDataList || [],
			});

			if (duplicates.length) {
				const preview = duplicates
					.slice(0, 8)
					.map((row) => `${row.store} (${row.columns.join(", ")})`)
					.join("; ");
				showSnack(
					`A store can exist in only one column. Duplicate store${duplicates.length > 1 ? "s" : ""}: ${preview}${
						duplicates.length > 8 ? "…" : ""
					}`,
					"error"
				);
				return;
			}

			if (!assignedCount) {
				showSnack("No matching store codes found in the Excel file", "error");
				return;
			}

			setProfileData((prev) => ({
				...prev,
				[logicProfileStoreGroupingAssoc]: {
					...prev?.[logicProfileStoreGroupingAssoc],
					...grouping,
				},
			}));

			const warningParts = [];
			if (unknown.length) {
				const preview = unknown.slice(0, 8).join(", ");
				warningParts.push(
					`${unknown.length} unknown store code${unknown.length > 1 ? "s" : ""}: ${preview}${
						unknown.length > 8 ? "…" : ""
					}`
				);
			}

			if (warningParts.length) {
				showSnack(`Store grouping updated. ${warningParts.join(". ")}`, "warning");
			} else {
				showSnack("Store grouping updated from Excel", "success");
			}
		} catch (error) {
			showSnack(error?.message || "Unable to read the Excel file", "error");
		}
	};

	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap" pb={1}>
				<Typography variant="body2" color="text.secondary">
					Upload store codes in To Allocate, To Exclude and FOC Stores columns, or drag stores below
				</Typography>
				<Box display="flex" gap={1}>
					<Button
						type="button"
						size="small"
						variant="outlined"
						startIcon={<FileDownloadOutlined />}
						onClick={(event) => {
							event.preventDefault();
							downloadStoreGroupingSample();
						}}
						sx={{ textTransform: "none" }}>
						Sample
					</Button>
					<Button
						type="button"
						size="small"
						variant="contained"
						startIcon={<UploadFileOutlined />}
						onClick={(event) => {
							event.preventDefault();
							fileInputRef.current?.click();
						}}
						sx={{ textTransform: "none" }}>
						Upload Excel
					</Button>
					<input
						ref={fileInputRef}
						type="file"
						hidden
						accept={ACCEPTED_EXCEL_TYPES}
						onChange={handleExcelUpload}
					/>
				</Box>
			</Box>
			<Box display="flex" justifyContent="center" gap={2} p={1}>
				{Object.keys(storeGroupingtypes)
					?.map((row) => storeGroupingtypes[row])
					.map((row) => {
						return {
							...row,
							list: profileData?.[logicProfileStoreGroupingAssoc]?.[row?.key] ?? [],
							setList: (data) => setProfileDataAfterDragging(data, row?.key),
						};
					})
					?.map(({ name, key, list, setList, ...rest }, index) => (
						<Card key={index} sx={{ width: 250, p: 2, background: grey[50], boxShadow: 1 }}>
							<Typography variant="h6" fontWeight="bold" mb={2}>
								{name} ({list?.length || 0})
							</Typography>
							<Box
								sx={{ height: 250, overflowY: "scroll", ...muiCustomScrollbar }}
								onDragOver={(e) => e.preventDefault()}
								onDrop={(e) => {
									const store = e.dataTransfer.getData("store");
									const source = e.dataTransfer.getData("source");
									if (source === key) return;
									handleDragEnd(
										e,
										null,
										store,
										profileData?.[logicProfileStoreGroupingAssoc]?.[source] ?? [],
										(data) => setProfileDataAfterDragging(data, source),
										list,
										setList
									);
								}}>
								{list?.map((store) => (
									<motion.div
										key={store}
										style={{
											display: "flex",
											alignItems: "center",
											padding: 10,
											marginBottom: 8,
											backgroundColor: "white",
											borderRadius: 4,
											boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.07)",
											cursor: "pointer",
										}}
										draggable
										onDragStart={(e) => {
											e.dataTransfer.setData("store", store);
											e.dataTransfer.setData("source", key);
										}}>
										<DragIndicator style={{ marginRight: 8, color: "gray" }} />
										<Tooltip
											title={`${storeFullDataListObj?.[store]?.[storeMastShortNameKey] || store} (${store})`}
											placement="bottom-start">
											<Typography noWrap>
												{storeFullDataListObj?.[store]?.[storeMastShortNameKey] || store}
											</Typography>
										</Tooltip>
									</motion.div>
								))}
							</Box>
						</Card>
					))}
			</Box>
		</Box>
	);
};
export default DragDropStores;
